import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { downloadCSV, getCSVString, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { Funds } from '@fortem-monorepo/web-sdk'
import { API } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'
import { queryKeys } from '@/react-query/queries/funds/keys'

export const useDeleteFundHolidayMutation = () => {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.funds.deleteHoliday(id),
    onSuccess: () => toast.success('Holiday deleted'),
    onError: (error) => toastApiError(error, 'Unable to delete holiday'),
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ['funds', 'holidays'] })
    },
  })
}

export const useCreateFundHolidaysMutation = () => {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Funds.CreateFundHolidaysBody) => api.funds.createHolidays(body),
    onSuccess: (rows) => {
      toast.success(
        rows.length ? `${rows.length} holiday${rows.length === 1 ? '' : 's'} added` : 'Selected holidays already exist',
      )
    },
    onError: (error) => toastApiError(error, 'Unable to add holidays'),
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ['funds', 'holidays'] })
    },
  })
}

export const useAddFundMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: Funds.CreateFundBody) => {
      return api.funds.create(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Fund created')
    },
  })
}

export const useUpdateFundMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: API.UpdateBody) => {
      return api.funds.update(body)
    },
    onSuccess: (_data, _params) => {
      toast.success(`Successfully updated ${_params.oldValue} to ${_params.newValue}`)
    },
    onError: (error, _params) => {
      toastApiError(error, `Error updating ${_params.oldValue} to ${_params.newValue}`)
    },
  })
}

export const useUpdateNavEntryMutation = (params?: Funds.GetNavParams) => {
  const api = useApi()

  const client = useQueryClient()

  return useMutation({
    mutationFn: (params: Funds.UpdateNavEntryBody) => api.funds.nav.updateNav(params),
    onSuccess: (_data, params, _context) => {
      toast.success(`Succesfully update ${params.oldValue} to ${params.newValue}.`)
    },
    onError: (error, params, _context) => {
      toastApiError(error, `Error updating ${params.oldValue} to ${params.newValue}.`)
    },
    onSettled: (_data, _error, _params) => {
      if (params?.st_dt) {
        client.invalidateQueries({ queryKey: queryKeys.nav(params) })
      }
    },
  })
}

export const useDeleteNavEntryMutation = (params?: Funds.GetNavParams) => {
  const api = useApi()

  const client = useQueryClient()

  return useMutation({
    mutationFn: (params: Funds.DeleteNavEntryBody) => api.funds.nav.deleteEntry(params),
    onSuccess: (_data, params, _context) => {
      toast.success(`Successfully deleted nav entry`, {
        description: `${params.fund_name} - ${params.value_date}`,
      })
    },
    onError: (error, params, _context) => {
      toastApiError(error, `Error deleting nav entry for fund ${params.fund_name} on ${params.value_date}`)
    },
    onSettled: (_data, _error, _params) => {
      if (params?.st_dt) {
        client.invalidateQueries({ queryKey: queryKeys.nav(params) })
      }
    },
  })
}

export const useDownloadSubscriptionRedemptionDataMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: async () => {
      const data = await api.funds.nav.getSubscriptionRedemptionData()
      const today = toISO8601(new Date()).replaceAll('-', '_')

      if (data) {
        downloadCSV(getCSVString(data), `subscription_redemption_${today}`)
      }
    },
  })
}
